import { useQuery } from "@tanstack/react-query";
import { Timestamp } from "firebase/firestore";
import { queryKeys } from "@/lib/queryClient";
import { toDate } from "@/lib/format";
import { formatRegion, loadRegionResolver } from "@/lib/geo";
import {
  OPEN_ORDER_STATUSES,
  type AppUser,
  type Order,
  type OrderStatus,
  type Seller,
  type SellerBillingStatus,
} from "@/types";
import { fetchCollection, orderBy, where } from "./use-firestore";
import { regionOfClient, regionOfSeller } from "./use-regions";

export const DASHBOARD_RANGES = {
  "7d": { label: "7 dias", days: 7 },
  "30d": { label: "30 dias", days: 30 },
  "90d": { label: "90 dias", days: 90 },
  "365d": { label: "12 meses", days: 365 },
} as const;

export type DashboardRange = keyof typeof DASHBOARD_RANGES;

export interface RegionCount {
  /** Sigla do estado ou código IBGE da cidade: estável mesmo com nomes iguais. */
  key: string;
  label: string;
  count: number;
}

/**
 * Um ponto do mapa do painel: a posição do endereço do cliente ou do
 * estabelecimento, sem arredondar. O mapa agrupado arredonda a do cliente na
 * hora de contar (ver PresenceMapCard); o de posições precisas usa esta.
 */
export interface MapPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  /** Sem coordenada no endereço: a posição é a sede da cidade cadastrada. */
  approximate: boolean;
  /** "Cidade, UF", para o rótulo do ponto. */
  region: string;
}

export interface SellerRanking {
  sellerId: string;
  name: string;
  revenue: number;
  orders: number;
}

export interface DayPoint {
  /** ISO curto, para o eixo do gráfico ordenar sozinho. */
  day: string;
  label: string;
  orders: number;
  revenue: number;
}

export interface DashboardData {
  range: DashboardRange;
  since: Date;
  /** Receita entregue no período: a base da comissão da plataforma. */
  deliveredRevenue: number;
  deliveredOrders: number;
  /** A comissão que essa receita gera, pela taxa vigente. */
  estimatedPlatformFee: number;
  platformFeePercentage: number;
  cancelledOrders: number;
  openOrders: number;
  ordersInPeriod: number;
  averageTicket: number;
  totalClients: number;
  totalSellers: number;
  activeSellers: number;
  openSellers: number;
  blockedSellers: number;
  sellersByBillingStatus: Record<string, number>;
  statusBreakdown: { status: OrderStatus; count: number }[];
  byState: RegionCount[];
  byCity: RegionCount[];
  /** Quantos registros ficaram sem região nenhuma (sem coordenada e sem texto). */
  withoutRegion: number;
  mapPoints: { clients: MapPoint[]; sellers: MapPoint[] };
  topSellers: SellerRanking[];
  daily: DayPoint[];
}

/** O mês em que o pedido foi ENTREGUE, que é o que o faturamento considera. */
function deliveredAt(order: Order): Date | null {
  const entry = order.statusHistory?.find((h) => h.status === "delivered");
  return toDate(entry?.timestamp) ?? (order.status === "delivered" ? toDate(order.updatedAt) : null);
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function bump(map: Map<string, number>, key: string | undefined, by = 1) {
  if (!key) return;
  map.set(key, (map.get(key) ?? 0) + by);
}

function countRegion(
  map: Map<string, RegionCount>,
  key: string | null | undefined,
  label: string,
) {
  if (!key) return;
  const row = map.get(key);
  if (row) row.count += 1;
  else map.set(key, { key, label, count: 1 });
}

function sortedRegions(map: Map<string, RegionCount>): RegionCount[] {
  return [...map.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label, "pt-BR"),
  );
}

/**
 * O painel gerencial.
 *
 * Tudo é calculado por consulta, sem documento agregado: o backend não mantém
 * nenhum. Enquanto o volume for o atual isso responde rápido; quando não for
 * mais, o caminho certo é um agregado mantido por trigger no
 * `zepadoca-functions`, e não paginar esta consulta. Registrado em
 * `docs/10-manutencao-e-granularidade.md`.
 *
 * A receita usada é a dos pedidos **entregues**, e a data que vale é a da
 * entrada `delivered` do `statusHistory`, não a de criação: é exatamente a
 * mesma régua do cron de faturamento, e é o que faz o número daqui bater com a
 * fatura que o vendedor recebe.
 */
export function useDashboard(range: DashboardRange, platformFeePercentage: number) {
  return useQuery({
    queryKey: [...queryKeys.dashboard(range), platformFeePercentage],
    queryFn: async (): Promise<DashboardData> => {
      const days = DASHBOARD_RANGES[range].days;
      const since = new Date();
      since.setHours(0, 0, 0, 0);
      since.setDate(since.getDate() - days);

      const [orders, sellers, clients, billingStatuses, resolver] = await Promise.all([
        fetchCollection<Order>(
          "orders",
          where("createdAt", ">=", Timestamp.fromDate(since)),
          orderBy("createdAt", "desc"),
        ),
        fetchCollection<Seller>("sellers"),
        fetchCollection<AppUser>("users"),
        fetchCollection<SellerBillingStatus>("sellersBillingStatus"),
        loadRegionResolver(),
      ]);

      const sellerName = new Map(sellers.map((s) => [s.id, s.name ?? "Sem nome"]));

      let deliveredRevenue = 0;
      let deliveredOrders = 0;
      let cancelledOrders = 0;
      let openOrders = 0;

      const statusMap = new Map<OrderStatus, number>();
      const revenueBySeller = new Map<string, number>();
      const ordersBySeller = new Map<string, number>();
      const dailyMap = new Map<string, DayPoint>();

      // Semeia todos os dias do período: sem isso, um dia sem pedido some do
      // gráfico e a linha liga dois pontos distantes como se o movimento
      // tivesse sido constante.
      for (let i = 0; i <= days; i += 1) {
        const d = new Date(since);
        d.setDate(d.getDate() + i);
        dailyMap.set(dayKey(d), {
          day: dayKey(d),
          label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
          orders: 0,
          revenue: 0,
        });
      }

      for (const order of orders) {
        const status = (order.status ?? "pending") as OrderStatus;
        statusMap.set(status, (statusMap.get(status) ?? 0) + 1);

        if (OPEN_ORDER_STATUSES.includes(status)) openOrders += 1;
        if (status === "cancelled") cancelledOrders += 1;

        const created = toDate(order.createdAt);
        if (created) {
          const point = dailyMap.get(dayKey(created));
          if (point) point.orders += 1;
        }

        if (status !== "delivered") continue;

        const total = Number(order.total ?? 0);
        deliveredRevenue += total;
        deliveredOrders += 1;
        bump(ordersBySeller, order.sellerId);
        bump(revenueBySeller, order.sellerId, total);

        const when = deliveredAt(order) ?? created;
        if (when) {
          const point = dailyMap.get(dayKey(when));
          if (point) point.revenue += total;
        }
      }

      // A região do CLIENTE vem do endereço padrão que ele guardou no próprio
      // documento; a do ESTABELECIMENTO, do cadastro dele. As duas entram na
      // mesma conta porque a pergunta que a tela responde é "onde o Zé Padoca
      // acontece", e não "onde moram os clientes".
      //
      // O agrupamento é pela coordenada do endereço, e não pelo texto: "MG" e
      // "Minas Gerais" contavam como dois estados, "SRS" e "Santa Rita do
      // Sapucaí" como duas cidades. Ver lib/geo.ts.
      const stateMap = new Map<string, RegionCount>();
      const cityMap = new Map<string, RegionCount>();
      const mapPoints: DashboardData["mapPoints"] = { clients: [], sellers: [] };
      let withoutRegion = 0;

      for (const client of clients) {
        const region = regionOfClient(client, resolver);
        if (!region || region.source === "nenhuma") {
          withoutRegion += 1;
          continue;
        }
        countRegion(stateMap, region.uf, `${region.stateName} (${region.uf})`);
        countRegion(cityMap, region.cityKey, formatRegion(region));
        if (region.latitude != null && region.longitude != null) {
          mapPoints.clients.push({
            id: client.id,
            name: client.name || "Cliente sem nome",
            lat: region.latitude,
            lng: region.longitude,
            approximate: region.source !== "coordenada",
            region: formatRegion(region),
          });
        }
      }
      for (const seller of sellers) {
        const region = regionOfSeller(seller, resolver);
        if (!region || region.source === "nenhuma") {
          withoutRegion += 1;
          continue;
        }
        countRegion(stateMap, region.uf, `${region.stateName} (${region.uf})`);
        countRegion(cityMap, region.cityKey, formatRegion(region));
        if (region.latitude != null && region.longitude != null) {
          mapPoints.sellers.push({
            id: seller.id,
            name: seller.name || "Sem nome",
            lat: region.latitude,
            lng: region.longitude,
            approximate: region.source !== "coordenada",
            region: formatRegion(region),
          });
        }
      }

      const sellersByBillingStatus: Record<string, number> = {};
      for (const s of billingStatuses) {
        const key = s.status ?? "Inactive";
        sellersByBillingStatus[key] = (sellersByBillingStatus[key] ?? 0) + 1;
      }

      const topSellers: SellerRanking[] = [...revenueBySeller.entries()]
        .map(([sellerId, revenue]) => ({
          sellerId,
          name: sellerName.get(sellerId) ?? "Estabelecimento removido",
          revenue,
          orders: ordersBySeller.get(sellerId) ?? 0,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      return {
        range,
        since,
        deliveredRevenue,
        deliveredOrders,
        estimatedPlatformFee: deliveredRevenue * (platformFeePercentage / 100),
        platformFeePercentage,
        cancelledOrders,
        openOrders,
        ordersInPeriod: orders.length,
        averageTicket: deliveredOrders > 0 ? deliveredRevenue / deliveredOrders : 0,
        totalClients: clients.length,
        totalSellers: sellers.length,
        activeSellers: sellers.filter((s) => s.isActive).length,
        openSellers: sellers.filter((s) => s.isOpen).length,
        blockedSellers: sellers.filter((s) => s.isActive === false && s.blockReason).length,
        sellersByBillingStatus,
        statusBreakdown: [...statusMap.entries()]
          .map(([status, count]) => ({ status, count }))
          .sort((a, b) => b.count - a.count),
        byState: sortedRegions(stateMap),
        byCity: sortedRegions(cityMap),
        withoutRegion,
        mapPoints,
        topSellers,
        daily: [...dailyMap.values()].sort((a, b) => a.day.localeCompare(b.day)),
      };
    },
  });
}
